import { useEffect, Children, isValidElement, Fragment } from 'react';

import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import Panel, { PANEL_SIZES } from '#globalComponents/Overlay/Panel';
import { LAYER } from '#lib/constants/layers';

const PANEL_SLOTS = new Set([Panel.Header, Panel.Body, Panel.Footer]);

/**
 * Children that compose their own Panel slots are rendered as-is; anything else gets a body.
 *
 * Looks through fragments, because a dialog that picks its slots per branch usually returns them
 * wrapped in one — missing that would nest its footer inside the scrolling body.
 */
function usesPanelSlots(children) {
	return Children.toArray(children).some((child) => {
		if (!isValidElement(child)) return false;
		if (child.type === Fragment) return usesPanelSlots(child.props.children);
		return PANEL_SLOTS.has(child.type);
	});
}

/**
 * Centered dialog. Renders a Panel over a backdrop.
 *
 * Compose the contents from Panel.Header / Panel.Body / Panel.Footer. Children that do not use
 * those slots are wrapped in a padded, scrollable body so a dialog never sits flush against the
 * panel edge; pass `title` to give such a dialog the standard header.
 */
export default function Modal({ isOpen, onClose, children, title, size = 'm', closeOnBackdrop = true, as, onSubmit }) {
	useEffect(() => {
		if (!isOpen) return undefined;
		const handleKeyDown = (e) => {
			if (e.key === 'Escape' && !e.defaultPrevented) onClose?.();
		};
		document.addEventListener('keydown', handleKeyDown);
		// Prevent the page behind the dialog from scrolling while it is open.
		const previousOverflow = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.removeEventListener('keydown', handleKeyDown);
			document.body.style.overflow = previousOverflow;
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const composed = usesPanelSlots(children);

	return createPortal(
		<div
			className={`fixed inset-0 ${LAYER.modal} flex items-center justify-center p-4 bg-text/40 backdrop-blur-sm`}
			onMouseDown={closeOnBackdrop ? (e) => e.target === e.currentTarget && onClose?.() : undefined}
		>
			<Panel
				as={as}
				onSubmit={onSubmit}
				role="dialog"
				aria-modal="true"
				className={`w-full ${PANEL_SIZES[size]} max-h-[90vh]`}
			>
				{title && <Panel.Header onClose={onClose}>{title}</Panel.Header>}
				{composed ? children : <Panel.Body>{children}</Panel.Body>}
			</Panel>
		</div>,
		document.body,
	);
}

Modal.propTypes = {
	isOpen: PropTypes.bool,
	onClose: PropTypes.func,
	children: PropTypes.node,
	title: PropTypes.node,
	size: PropTypes.oneOf(['s', 'm', 'l', 'xl']),
	as: PropTypes.elementType,
	onSubmit: PropTypes.func,
	closeOnBackdrop: PropTypes.bool,
};
